'use client';

import { ArrowForward, ChevronLeft, ChevronRight } from '@mui/icons-material';
import { Box, IconButton, Typography } from '@mui/material';
import Image from 'next/image';
import { useEffect, useRef, useState } from 'react';

import { Button } from '@/components/atoms';
import OrangeLine from '@/components/atoms/OrangeLine';
import { SectionContainer } from '@/components/layouts/SectionContainer';
import { FeaturedProjects } from '@/services/types';
import { getImageAlt, getImageUrl } from '@/services/utils';

interface FeaturedProjectsSectionProps {
  data?: FeaturedProjects;
  className?: string;
  id?: string; // Optional ID for easier debugging
}

export default function FeaturedProjectsSection({
  data,
  className = '',
  id,
}: FeaturedProjectsSectionProps) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [showNavigation, setShowNavigation] = useState(false);
  const sliderRef = useRef<HTMLDivElement>(null);

  const fallbackData: FeaturedProjects = {
    id: 1,
    title: 'DỰ ÁN TIÊU BIỂU',
    projects: [
      {
        id: 1,
        title: 'Dự án mẫu 1',
        description: 'Mô tả dự án mẫu 1',
        image: {
          id: 1,
          name: 'project-1',
          url: '/api/uploads/project-1.jpg',
          width: 420,
          height: 568,
        },
        order: 1,
      },
      {
        id: 2,
        title: 'Dự án mẫu 2',
        description: 'Mô tả dự án mẫu 2',
        image: {
          id: 2,
          name: 'project-2',
          url: '/api/uploads/project-2.jpg',
          width: 420,
          height: 568,
        },
        order: 2,
      },
    ],
  };

  const featuredProjects = data || fallbackData;
  const projects = featuredProjects.projects || [];
  const totalProjects = projects.length;

  useEffect(() => {
    setShowNavigation(totalProjects > 1);
  }, [totalProjects]);

  const goToPrevious = () => {
    setCurrentIndex(prev => (prev > 0 ? prev - 1 : totalProjects - 1));
  };

  const goToNext = () => {
    setCurrentIndex(prev => (prev < totalProjects - 1 ? prev + 1 : 0));
  };

  useEffect(() => {
    if (sliderRef.current && totalProjects > 1) {
      const cardWidth = 460;
      const scrollPosition = currentIndex * cardWidth;

      sliderRef.current.scrollTo({
        left: scrollPosition,
        behavior: 'smooth',
      });
    }
  }, [currentIndex, totalProjects]);

  useEffect(() => {
    if (!showNavigation) return;

    const interval = setInterval(() => {
      goToNext();
    }, 5000);

    return () => clearInterval(interval);
  }, [showNavigation, currentIndex]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'ArrowLeft') {
        goToPrevious();
      } else if (event.key === 'ArrowRight') {
        goToNext();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [totalProjects]);

  if (totalProjects === 0) {
    return null;
  }

  return (
    <SectionContainer>
      <Box
        component='section'
        id={id || 'featured-projects-section'}
        data-component='featured-projects-section'
        data-total-projects={totalProjects}
        data-current-index={currentIndex}
        data-show-navigation={showNavigation}
        data-has-data={!!data}
        data-fallback-used={!data}
        sx={{ py: 8, backgroundColor: 'white' }}
        className={className}
      >
        <Box
          sx={{ textAlign: 'left', mb: 6 }}
          data-component='featured-projects-header'
        >
          <Typography
            variant='h2'
            component='h2'
            data-component='featured-projects-title'
            sx={{
              fontFamily: 'var(--font-inter), Inter, sans-serif',
              fontWeight: 700,
              fontSize: {
                xs: '1.75rem',
                sm: '2.25rem',
                md: '3rem',
                lg: '40px',
              },
              lineHeight: 1.2,
              color: 'primary.main',
              mb: 3,
              textTransform: 'uppercase',
            }}
          >
            {featuredProjects.title}
          </Typography>
          <OrangeLine width='340px' height='5px' mx='none' />
        </Box>

        <Box
          sx={{ position: 'relative' }}
          data-component='featured-projects-slider-container'
        >
          <Box
            ref={sliderRef}
            data-component='featured-projects-slider'
            data-current-index={currentIndex}
            data-total-projects={totalProjects}
            sx={{
              display: 'flex',
              gap: '40px',
              overflow: 'hidden',
              scrollBehavior: 'smooth',
              '&::-webkit-scrollbar': { display: 'none' },
              scrollbarWidth: 'none',
              msOverflowStyle: 'none',
              minWidth: '100%',
              paddingRight: '40px',
            }}
          >
            {projects.map((project, index) => (
              <Box
                key={project.id}
                data-component='featured-project-card'
                data-project-id={project.id}
                data-project-index={index}
                data-is-current={index === currentIndex}
                sx={{
                  flexShrink: 0,
                  width: project.image?.width === 495 ? '495px' : '420px',
                  position: 'relative',
                }}
              >
                <Box
                  sx={{
                    height: '126px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mb: 2,
                  }}
                  data-component='featured-project-number'
                >
                  <Typography
                    variant='h1'
                    component='h3'
                    data-component='featured-project-number-text'
                    sx={{
                      fontFamily: 'var(--font-inter), Inter, sans-serif',
                      fontWeight: 800,
                      fontSize: {
                        xs: '3rem',
                        sm: '4rem',
                        md: '5rem',
                        lg: '120px',
                      },
                      lineHeight: 1.05,
                      color:
                        index === currentIndex
                          ? 'primary.main'
                          : 'rgba(0, 26, 68, 0.2)',
                      textTransform: 'uppercase',
                    }}
                  >
                    {String(index + 1).padStart(2, '0')}.
                  </Typography>
                </Box>

                <Box
                  data-component='featured-project-image-container'
                  data-project-id={project.id}
                  data-has-image={!!project.image?.url}
                  sx={{
                    width: '100%',
                    height: '568px',
                    position: 'relative',
                    borderRadius: '10px',
                    overflow: 'hidden',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      '& .content-overlay': {
                        background: 'white',
                        '& .project-description': {
                          display: '-webkit-box',
                          opacity: 1,
                        },
                        '& .project-link': {
                          display: 'flex',
                          opacity: 1,
                        },
                        '& .project-title': {
                          color: 'text.primary',
                        },
                        '& .project-divider': {
                          opacity: 1,
                        },
                      },
                    },
                  }}
                >
                  {project.image?.url ? (
                    <Box
                      data-component='featured-project-image'
                      data-image-url={getImageUrl(project.image)}
                      sx={{
                        width: '100%',
                        height: '100%',
                        position: 'relative',
                      }}
                    >
                      <Image
                        src={getImageUrl(project.image)}
                        alt={getImageAlt(project.image)}
                        fill
                        priority={index === 0}
                        sizes='(max-width: 768px) 100vw, 460px'
                        style={{
                          objectFit: 'cover',
                        }}
                      />
                    </Box>
                  ) : (
                    <Box
                      data-component='featured-project-no-image'
                      sx={{
                        width: '100%',
                        height: '100%',
                        backgroundColor: 'grey.100',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'text.secondary',
                      }}
                    >
                      No Image
                    </Box>
                  )}

                  <Box
                    className='content-overlay'
                    data-component='featured-project-overlay'
                    sx={{
                      position: 'absolute',
                      bottom: 0,
                      left: 0,
                      right: 0,
                      background:
                        'linear-gradient(180deg, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 0.9) 100%)',
                      borderRadius: '0px 0px 10px 10px',
                      padding: '24px',
                      transition: 'all 0.3s ease',
                    }}
                  >
                    <Typography
                      className='project-title'
                      variant='h3'
                      data-component='featured-project-title'
                      data-project-title={project.title}
                      sx={{
                        fontFamily: 'var(--font-inter), Inter, sans-serif',
                        fontWeight: 700,
                        fontSize: {
                          xs: '1rem',
                          sm: '1.125rem',
                          lg: '18px',
                        },
                        lineHeight: 1.56,
                        mb: 2,
                        minHeight: '56px',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        color: 'white',
                        transition: 'color 0.3s ease',
                      }}
                    >
                      {project.title}
                    </Typography>

                    <Box
                      className='project-divider'
                      data-component='featured-project-divider'
                      sx={{
                        width: '100%',
                        height: '1px',
                        backgroundColor: 'divider',
                        mb: 2,
                        opacity: 0,
                        transition: 'all 0.3s ease',
                      }}
                    />

                    <Typography
                      className='project-description'
                      variant='body2'
                      data-component='featured-project-description'
                      data-project-description={project.description}
                      sx={{
                        fontFamily: 'var(--font-inter), Inter, sans-serif',
                        fontWeight: 400,
                        fontSize: {
                          xs: '0.875rem',
                          lg: '16px',
                        },
                        lineHeight: 1.5,
                        color: 'text.secondary',
                        minHeight: '96px',
                        display: 'none',
                        overflow: 'hidden',
                        mb: 3,
                        opacity: 0,
                        transition: 'all 0.3s ease',
                      }}
                    >
                      {project.description}
                    </Typography>

                    <Box
                      className='project-link'
                      data-component='featured-project-link'
                      sx={{
                        display: 'none',
                        alignItems: 'center',
                        gap: '16px',
                        opacity: 0,
                        transition: 'all 0.3s ease',
                      }}
                    >
                      <Button
                        variant='outline'
                        size='small'
                        icon={<ArrowForward />}
                        iconPosition='end'
                        data-component='featured-project-cta-button'
                      >
                        Tìm hiểu thêm
                      </Button>
                    </Box>

                    <Box
                      data-component='featured-project-indicator'
                      data-is-current={index === currentIndex}
                      sx={{
                        position: 'absolute',
                        bottom: 0,
                        left: 0,
                        right: 0,
                        width: '100%',
                        height: '8px',
                        backgroundColor: 'secondary.main',
                        borderRadius: '0px 0px 10px 10px',
                        display: index === currentIndex ? 'block' : 'none',
                        opacity: index === currentIndex ? 1 : 0,
                        transition: 'all 0.3s ease',
                      }}
                    />
                  </Box>
                </Box>
              </Box>
            ))}
          </Box>

          {showNavigation && (
            <Box
              data-component='featured-projects-navigation'
              data-current-index={currentIndex}
              data-total-projects={totalProjects}
              sx={{
                position: 'absolute',
                bottom: '-48px',
                left: '50%',
                transform: 'translateX(-50%)',
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                zIndex: 2,
              }}
            >
              <Box
                data-component='featured-projects-progress-bar'
                sx={{
                  width: { xs: '120px', sm: '180px', lg: '240px' },
                  height: '2px',
                  backgroundColor: 'rgba(0, 0, 0, 0.25)',
                  position: 'relative',
                  borderRadius: '1px',
                }}
              >
                <Box
                  data-component='featured-projects-progress-fill'
                  sx={{
                    height: '100%',
                    backgroundColor: 'primary.main',
                    borderRadius: '1px',
                    transition: 'width 0.5s ease-out',
                    width: `${((currentIndex + 1) / totalProjects) * 100}%`,
                  }}
                />
              </Box>

              <IconButton
                onClick={goToPrevious}
                data-component='featured-projects-prev-button'
                aria-label='Previous featured project'
                sx={{
                  width: '24px',
                  height: '24px',
                  color: 'secondary.main',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 94, 19, 0.1)',
                  },
                }}
              >
                <ChevronLeft sx={{ fontSize: '24px' }} />
              </IconButton>

              <Typography
                variant='body2'
                data-component='featured-projects-counter'
                sx={{
                  fontFamily: 'var(--font-inter), Inter, sans-serif',
                  fontWeight: 400,
                  fontSize: {
                    xs: '0.75rem',
                    lg: '14px',
                  },
                  lineHeight: 1.21,
                  color: 'primary.main',
                  textTransform: 'uppercase',
                  minWidth: '38px',
                  textAlign: 'center',
                }}
              >
                {currentIndex + 1} - {totalProjects}
              </Typography>

              <IconButton
                onClick={goToNext}
                data-component='featured-projects-next-button'
                aria-label='Next featured project'
                sx={{
                  width: '24px',
                  height: '24px',
                  color: 'secondary.main',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 94, 19, 0.1)',
                  },
                }}
              >
                <ChevronRight sx={{ fontSize: '24px' }} />
              </IconButton>
            </Box>
          )}
        </Box>
      </Box>
    </SectionContainer>
  );
}
